import { icons } from './icons'

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T

// --- Element accessors ---
export const els = {
  // Screens
  landingScreen:  () => $<HTMLDivElement>('landing-screen'),
  lobbyScreen:    () => $<HTMLDivElement>('lobby-screen'),
  meetingScreen:  () => $<HTMLDivElement>('meeting-screen'),

  // Landing
  createMeetingBtn: () => $<HTMLButtonElement>('create-meeting-btn'),
  codeInput:        () => $<HTMLInputElement>('code-input'),
  joinCodeBtn:      () => $<HTMLButtonElement>('join-code-btn'),

  // Lobby
  lobbyBackBtn:   () => $<HTMLButtonElement>('lobby-back-btn'),
  lobbyVideo:     () => $<HTMLVideoElement>('lobby-video'),
  lobbyPlaceholder: () => $<HTMLDivElement>('lobby-placeholder'),
  micSelect:      () => $<HTMLSelectElement>('mic-select'),
  camSelect:      () => $<HTMLSelectElement>('cam-select'),
  lobbyCode:      () => $<HTMLDivElement>('lobby-code'),
  copyLinkBtn:    () => $<HTMLButtonElement>('copy-link-btn'),
  nameInput:      () => $<HTMLInputElement>('name-input'),
  lobbyJoinBtn:   () => $<HTMLButtonElement>('lobby-join-btn'),

  // Meeting
  videoGrid:      () => $<HTMLDivElement>('video-grid'),
  micBtn:         () => $<HTMLButtonElement>('mic-btn'),
  camBtn:         () => $<HTMLButtonElement>('cam-btn'),
  shareBtn:       () => $<HTMLButtonElement>('share-btn'),
  leaveBtn:       () => $<HTMLButtonElement>('leave-btn'),
  status:         () => $<HTMLDivElement>('status'),

  // Participant panel
  participantsToggleBtn: () => $<HTMLButtonElement>('participants-toggle-btn'),
  participantPanel:      () => $<HTMLDivElement>('participant-panel'),
  participantCount:      () => $<HTMLSpanElement>('participant-count'),
  participantList:       () => $<HTMLDivElement>('participant-list'),
  panelCloseBtn:         () => $<HTMLButtonElement>('panel-close-btn'),
}

// --- Inject static icons into spans ---
export function initIcons(): void {
  const set = (id: string, html: string) => {
    const el = document.getElementById(id)
    if (el) el.innerHTML = html
  }
  set('lobby-back-icon', icons.arrowLeft)
  set('copy-icon', icons.copy)
  set('mic-btn-icon', icons.mic)
  set('cam-btn-icon', icons.camera)
  set('share-btn-icon', icons.screenShare)
  set('leave-btn-icon', icons.phoneOff)
  set('participants-icon', icons.users)
  set('panel-close-icon', icons.x)
}

// --- Screen transitions ---
export function showLanding(): void {
  els.landingScreen().classList.add('active')
  els.lobbyScreen().classList.remove('active')
  els.meetingScreen().classList.remove('active')
}

export function showLobby(): void {
  els.landingScreen().classList.remove('active')
  els.lobbyScreen().classList.add('active')
  els.meetingScreen().classList.remove('active')
}

export function showMeeting(): void {
  els.landingScreen().classList.remove('active')
  els.lobbyScreen().classList.remove('active')
  els.meetingScreen().classList.add('active')
}

// --- Deterministic color from ID ---
export function hashColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) - h + id.charCodeAt(i)) | 0
  }
  const hue = ((h % 360) + 360) % 360
  return `hsl(${hue}, 60%, 45%)`
}

// --- Video tiles ---
export function addVideoTile(
  stream: MediaStream | null,
  id: string,
  displayName: string,
  local = false,
): HTMLDivElement {
  // Deduplicate
  const existing = document.getElementById(`tile-${id}`)
  if (existing) {
    const video = existing.querySelector('video')!
    if (stream) video.srcObject = stream
    return existing as HTMLDivElement
  }

  const tile = document.createElement('div')
  tile.className = 'video-tile'
  tile.id = `tile-${id}`
  if (local) tile.classList.add('local')

  // Video element
  const video = document.createElement('video')
  video.autoplay = true
  video.playsInline = true
  if (local) video.muted = true
  if (stream) video.srcObject = stream
  tile.appendChild(video)

  // Avatar fallback
  const avatar = document.createElement('div')
  avatar.className = 'tile-avatar'
  const circle = document.createElement('div')
  circle.className = 'tile-avatar-circle'
  circle.style.background = hashColor(id)
  circle.textContent = (displayName || id).charAt(0)
  avatar.appendChild(circle)
  tile.appendChild(avatar)

  // Name bar
  const nameBar = document.createElement('div')
  nameBar.className = 'tile-name-bar'
  const micSpan = document.createElement('span')
  micSpan.className = 'tile-mic-icon'
  micSpan.innerHTML = icons.mic
  nameBar.appendChild(micSpan)
  const nameText = document.createElement('span')
  nameText.className = 'tile-name-text'
  nameText.textContent = local ? `${displayName} (You)` : displayName
  nameBar.appendChild(nameText)
  tile.appendChild(nameBar)

  // If no stream, show as video-off
  if (!stream) tile.classList.add('video-off')

  els.videoGrid().appendChild(tile)
  updateGrid()
  return tile
}

export function removeVideoTile(id: string): void {
  document.getElementById(`tile-${id}`)?.remove()
  updateGrid()
}

export function updateTileState(
  id: string,
  state: { audioMuted?: boolean; videoMuted?: boolean; speaking?: boolean },
): void {
  const tile = document.getElementById(`tile-${id}`)
  if (!tile) return

  if (state.videoMuted !== undefined) {
    tile.classList.toggle('video-off', state.videoMuted)
  }
  if (state.audioMuted !== undefined) {
    const micIcon = tile.querySelector('.tile-mic-icon')
    if (micIcon) {
      micIcon.innerHTML = state.audioMuted ? icons.micOff : icons.mic
      micIcon.classList.toggle('mic-off-icon', state.audioMuted)
    }
  }
  if (state.speaking !== undefined) {
    tile.classList.toggle('speaking', state.speaking)
  }
}

export function updateTileName(id: string, name: string, local = false): void {
  const tile = document.getElementById(`tile-${id}`)
  if (!tile) return
  const nameText = tile.querySelector('.tile-name-text')
  if (nameText) nameText.textContent = local ? `${name} (You)` : name
  const circle = tile.querySelector('.tile-avatar-circle')
  if (circle) circle.textContent = (name || id).charAt(0)
}

function updateGrid(): void {
  const grid = els.videoGrid()
  const count = grid.children.length
  grid.setAttribute('data-count', String(count))
}

// --- Icon-based toggle ---
export function updateToggle(btn: HTMLButtonElement, type: 'mic' | 'cam', on: boolean): void {
  const iconSpan = btn.querySelector('span')
  if (!iconSpan) return
  if (type === 'mic') {
    iconSpan.innerHTML = on ? icons.mic : icons.micOff
  } else {
    iconSpan.innerHTML = on ? icons.camera : icons.cameraOff
  }
  btn.classList.toggle('off', !on)
}

// --- Status ---
export function setStatus(msg: string): void {
  els.status().textContent = msg
}

// --- Error banner ---
export function showError(msg: string): void {
  let banner = document.getElementById('error-banner')
  if (!banner) {
    banner = document.createElement('div')
    banner.id = 'error-banner'
    banner.className = 'error-banner'
    banner.title = 'Click to dismiss'
    banner.addEventListener('click', () => banner!.remove())
  }
  banner.textContent = msg
  document.body.prepend(banner)
}

// --- Participant panel ---
export function addParticipant(id: string, name: string, audioMuted = false): void {
  if (document.getElementById(`participant-${id}`)) return

  const item = document.createElement('div')
  item.className = 'participant-item'
  item.id = `participant-${id}`

  const micSpan = document.createElement('span')
  micSpan.className = audioMuted ? 'mic-off-icon' : ''
  micSpan.innerHTML = audioMuted ? icons.micOff : icons.mic
  item.appendChild(micSpan)

  const nameSpan = document.createElement('span')
  nameSpan.className = 'participant-name'
  nameSpan.textContent = name
  item.appendChild(nameSpan)

  els.participantList().appendChild(item)
  updateParticipantCount()
}

export function removeParticipant(id: string): void {
  document.getElementById(`participant-${id}`)?.remove()
  updateParticipantCount()
}

export function updateParticipantMic(id: string, audioMuted: boolean): void {
  const item = document.getElementById(`participant-${id}`)
  if (!item) return
  const micSpan = item.querySelector('span')
  if (micSpan) {
    micSpan.className = audioMuted ? 'mic-off-icon' : ''
    micSpan.innerHTML = audioMuted ? icons.micOff : icons.mic
  }
}

function updateParticipantCount(): void {
  els.participantCount().textContent = String(els.participantList().children.length)
}

export function togglePanel(): void {
  els.participantPanel().classList.toggle('open')
}

export function closePanel(): void {
  els.participantPanel().classList.remove('open')
}

// --- Device selectors ---
export function populateDeviceSelect(
  select: HTMLSelectElement,
  devices: MediaDeviceInfo[],
  currentDeviceId?: string,
): void {
  select.innerHTML = ''
  for (const d of devices) {
    const opt = document.createElement('option')
    opt.value = d.deviceId
    opt.textContent = d.label || `Device ${select.options.length + 1}`
    if (currentDeviceId && d.deviceId === currentDeviceId) opt.selected = true
    select.appendChild(opt)
  }
}

// --- Lobby preview ---
export function setLobbyPreview(stream: MediaStream | null): void {
  const video = els.lobbyVideo()
  const placeholder = els.lobbyPlaceholder()
  if (stream) {
    video.srcObject = stream
    video.style.display = ''
    placeholder.style.display = 'none'
  } else {
    video.srcObject = null
    video.style.display = 'none'
    placeholder.style.display = ''
  }
}

// --- Reset meeting screen ---
export function clearMeeting(): void {
  els.videoGrid().innerHTML = ''
  updateGrid()
  els.participantList().innerHTML = ''
  updateParticipantCount()
  closePanel()
  setStatus('')
}
